import React, { useEffect, useState } from 'react';
import { useChatStore } from '../../hooks/useChatStore';
import { PushNav } from '../../services/notifications';
import StoriesBar from '../Stories/StoriesBar';
import { getAvatarEmoji } from '../Chat/ChatWindow';
import { absUrl } from '../../services/api';
import '../../styles/chatlist.css';

function lastMsgPreview(msg) {
  const t = msg.type;
  if (t === 'VIDEO_MESSAGE') return 'Видеосообщение';
  if (t === 'AUDIO_MESSAGE') return 'Аудиосообщение';
  if (t === 'IMAGE') return 'Изображение';
  if (t === 'STICKER') return 'Стикер';
  if (t === 'CALL_HISTORY') return 'Звонок';
  if (t === 'VIDEO') return msg.attachments?.[0]?.fileName || 'Видео';
  if (t === 'AUDIO') return msg.attachments?.[0]?.fileName || 'Аудио';
  if (msg.attachments?.[0]?.fileName) return msg.attachments[0].fileName;
  return (msg.content || '').substring(0, 30);
}

export default function ChatList({ onSettings, onAI, user, onLogout, activeAIChat, onOpenAIChat, onCloseAIChat, onOpenScheduled }) {
  const { chats, fetchChats, setActiveChat, activeChat, unreadCounts, onlineUsers, typingUsers, startDirectChat, createGroupChat, isConnected, channels, fetchChannels, createChannel, subscribeChannel, fetchError, channelsError } = useChatStore();
  const [showStartChat, setShowStartChat] = useState(false);
  const [startChatEmail, setStartChatEmail] = useState('');
  const [startChatResults, setStartChatResults] = useState([]);
  const [startChatError, setStartChatError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteBanner, setInviteBanner] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupParticipants, setGroupParticipants] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [groupSearchResults, setGroupSearchResults] = useState([]);
  const [groupError, setGroupError] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showChannels, setShowChannels] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [channelVisibility, setChannelVisibility] = useState('PUBLIC');
  const [channelComments, setChannelComments] = useState(false);
  const [channelAvatarUrl, setChannelAvatarUrl] = useState('');
  const [channelError, setChannelError] = useState('');
  const channelAvatarInputRef = React.useRef(null);

  useEffect(() => { fetchChats().catch(() => {}); }, []);

  useEffect(() => {
    const onFocus = () => {
      if (PushNav.chatId === '__ai__') {
        PushNav.chatId = null;
        setMobileOpen(false);
        onOpenAIChat?.();
        return;
      }
      if (PushNav.chatId) {
        const cid = PushNav.chatId;
        PushNav.chatId = null;
        setActiveChat(cid);
        setMobileOpen(false);
        onCloseAIChat?.();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const handleStartChatSearch = async (e) => {
    const q = e.target.value;
    setStartChatEmail(q);
    setStartChatError('');
    if (q.length > 2) {
      try {
        const api = (await import('../../services/api')).default;
        const res = await api.get('/users/search', { params: { q } });
        setStartChatResults(res.data);
      } catch (err) {
        setStartChatError('Ошибка поиска');
        setStartChatResults([]);
      }
    } else {
      setStartChatResults([]);
    }
  };

  const startChat = async (userId) => {
    try {
      const result = await startDirectChat(userId);
      if (result.type === 'existing' || result.type === 'chat_created') {
        if (result.chat) setActiveChat(result.chat.id);
      } else if (result.type === 'request_sent') {
        alert('Запрос на чат отправлен. Вы будете уведомлены при принятии.');
      }
      setStartChatEmail('');
      setStartChatResults([]);
      setShowStartChat(false);
      setMobileOpen(false);
    } catch (err) {
      setStartChatError(err.response?.data?.message || 'Ошибка создания запроса');
    }
  };

  const createInviteLink = async () => {
    try {
      const api = (await import('../../services/api')).default;
      const res = await api.post('/invites', { email: startChatEmail.trim() });
      const invite = res.data;
      const url = `${window.location.origin}/register?invite=${invite.token}&email=${encodeURIComponent(invite.email)}`;
      setInviteUrl(url);
      setInviteBanner(true);
      alert(`Приглашение отправлено на ${invite.email}`);
      setStartChatEmail('');
      setStartChatResults([]);
    } catch (err) {
      setStartChatError(err.response?.data?.message || 'Не удалось создать приглашение');
    }
  };

  const handleGroupSearch = async (e) => {
    const q = e.target.value;
    setGroupSearch(q);
    if (q.length > 2) {
      try {
        const api = (await import('../../services/api')).default;
        const res = await api.get('/users/search', { params: { q } });
        setGroupSearchResults(res.data.filter((u) => !groupParticipants.find((p) => p.id === u.id)));
      } catch {}
    } else {
      setGroupSearchResults([]);
    }
  };

  const addGroupParticipant = (u) => {
    setGroupParticipants((prev) => [...prev, u]);
    setGroupSearch('');
    setGroupSearchResults([]);
  };

  const removeGroupParticipant = (id) => {
    setGroupParticipants((prev) => prev.filter((p) => p.id !== id));
  };

  const createGroup = async () => {
    if (!groupName.trim()) { setGroupError('Введите название группы'); return; }
    if (groupParticipants.length === 0) { setGroupError('Добавьте хотя бы одного участника'); return; }
    try {
      const chat = await createGroupChat(groupName, groupParticipants.map((p) => p.id));
      setActiveChat(chat.id);
      setShowCreateGroup(false);
      setGroupName('');
      setGroupParticipants([]);
      setGroupError('');
      setMobileOpen(false);
    } catch (err) {
      setGroupError(err.response?.data?.message || 'Ошибка создания группы');
    }
  };

  const openChannels = async () => {
    setShowChannels(true);
    setShowCreateChannel(false);
    try {
      await fetchChannels();
    } catch (e) {}
  };

  const handleCreateChannel = async () => {
    if (!channelName.trim()) { setChannelError('Введите название канала'); return; }
    try {
      const chat = await createChannel(channelName.trim(), channelVisibility, channelComments, channelAvatarUrl || null);
      setActiveChat(chat.id);
      setShowCreateChannel(false);
      setShowChannels(false);
      setChannelName('');
      setChannelAvatarUrl('');
      setChannelError('');
      setMobileOpen(false);
      onCloseAIChat?.();
    } catch (err) {
      setChannelError(err.response?.data?.message || 'Ошибка создания канала');
    }
  };

  const handleChannelAvatarFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const api = (await import('../../services/api')).default;
      const res = await api.post('/files/upload', formData);
      setChannelAvatarUrl(res.data?.url || '');
    } catch (err) {
      setChannelError('Не удалось загрузить аватарку');
    }
    e.target.value = '';
  };

  return (
    <>
      <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
      <div className={`chatlist ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="chatlist-header">
          <div className="chatlist-logo">
            <img src="/tryndi_icon.jpeg" alt="Трынди" />
            <img src="/tryndi.png" className="chatlist-wordmark" alt="Трынди" />
          </div>
          <div className="chatlist-actions">
            <button className="icon-btn" onClick={onOpenScheduled} title="Запланированные сообщения">⏰</button>
            <button className="icon-btn" onClick={() => { onOpenAIChat(); setMobileOpen(false); }} title="AI Ассистент">🤖</button>
            <button className="icon-btn" onClick={() => { onSettings(); setMobileOpen(false); }} title="Настройки">⚙️</button>
            <button className="icon-btn logout-btn" onClick={onLogout} title="Выйти">🚪</button>
          </div>
        </div>
        <div className="chatlist-user-info">
          <div style={{ position: 'relative' }}>
            {user?.avatarUrl ? (
              <img src={absUrl(user.avatarUrl)} alt="" className="avatar-sm"
                onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
            ) : (
              <div className="avatar-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', background: '#e8e8ff', borderRadius: '50%' }}>
                {getAvatarEmoji(user?.displayName || user?.username)}
              </div>
            )}
            <span className={`network-dot ${isConnected ? 'online' : 'offline'}`}></span>
          </div>
          <div className="chatlist-user-name">{user?.displayName || user?.username}</div>
          <div className="chatlist-user-status">{user?.statusText || user?.status || 'В сети'}</div>
        </div>
        <StoriesBar />
        <div className="chatlist-actions-btns">
          <button className="action-btn" onClick={() => setShowStartChat(true)}>Начать чат</button>
          <button className="action-btn" style={{ marginTop: '8px' }} onClick={() => setShowCreateGroup(true)}>+ Групповой чат</button>
          <button className="action-btn" style={{ marginTop: '8px' }} onClick={openChannels}>📢 Каналы</button>
        </div>
        {showStartChat && (
          <div className="modal-overlay" onClick={() => setShowStartChat(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Начать чат</h3>
              <p style={{ fontSize: '13px', color: 'var(--system-text, #666)', marginTop: '0' }}>Введите email или имя пользователя</p>
              {startChatError && <div className="search-error">{startChatError}</div>}
              <div className="form-group">
                <input type="text" value={startChatEmail} onChange={handleStartChatSearch}
                  placeholder="Поиск по email..." style={{ width: '100%', padding: '8px', marginTop: '4px', boxSizing: 'border-box' }} />
                {startChatResults.length > 0 && (
                  <div className="search-results" style={{ position: 'relative', marginTop: '4px' }}>
                    {startChatResults.map((u) => (
                      <div key={u.id} className="search-result-item" onClick={() => startChat(u.id)}>
                        {u.avatarUrl ? (
                          <img src={absUrl(u.avatarUrl)} alt="" className="avatar-sm"
                            onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
                        ) : (
                          <div className="avatar-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', background: '#e8e8ff', borderRadius: '50%' }}>
                            {getAvatarEmoji(u.displayName)}
                          </div>
                        )}
                        <div>
                          <div className="search-name">{u.displayName}{u.isBlocked && ' 🚫'}</div>
                          <div className="search-email">{u.email}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {startChatResults.length === 0 && startChatEmail.length > 2 && !startChatEmail.includes('@') && (
                  <div style={{ marginTop: '12px' }}>
                    <div className="user-info-status muted">Ничего не найдено. Проверьте email или имя.</div>
                  </div>
                )}
                {startChatResults.length === 0 && startChatEmail.length > 5 && startChatEmail.includes('@') && (
                  <div style={{ marginTop: '12px' }}>
                    <div className="user-info-status muted">Пользователь не найден в системе</div>
                    <button className="action-btn" style={{ width: '100%', marginTop: '8px' }} onClick={createInviteLink}>
                      ✉️ Пригласить на {startChatEmail}
                    </button>
                    {inviteBanner && <div className="user-info-status" style={{ marginTop: '8px' }}>Ссылка готова: {inviteUrl}</div>}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button onClick={() => { setShowStartChat(false); setStartChatEmail(''); setStartChatResults([]); setStartChatError(''); }}
                  style={{ flex: 1, padding: '10px', background: 'var(--hover-bg, #f0f0f0)', color: 'var(--text, #333)', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Закрыть</button>
              </div>
            </div>
          </div>
        )}
        <div className="chatlist-items">
          {fetchError && (
            <div className="chatlist-error">
              <span>{fetchError}</span>
              <button className="chatlist-retry-btn" onClick={() => fetchChats().catch(() => {})}>Повторить</button>
            </div>
          )}
          <div
            className={`chatlist-item ai-chat-item ${activeAIChat ? 'active' : ''}`}
            onClick={() => { onOpenAIChat(); setMobileOpen(false); }}
          >
            <div className="chat-item-avatar">
              <div className="ai-avatar">🤖</div>
            </div>
            <div className="chat-item-info">
              <div className="chat-item-name">AI Ассистент</div>
              <div className="chat-item-last-msg">Генерация изображений</div>
            </div>
          </div>
          {chats.filter(c => c.name === 'Своё').map((chat) => (
            <div key={chat.id}
              className={`chatlist-item self-chat-item ${activeChat === chat.id ? 'active' : ''}`}
              onClick={() => { setActiveChat(chat.id); setMobileOpen(false); onCloseAIChat?.(); }}>
              <div className="chat-item-avatar">
                <img src={absUrl(user?.avatarUrl) || '/cat.svg'} alt="" onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
                <span className="self-chat-badge">☁️</span>
              </div>
              <div className="chat-item-info">
                <div className="chat-item-name">Своё</div>
                <div className="chat-item-last-msg">{typingUsers[chat.id] ? 'Печатает...' : (chat.lastMessage ? lastMsgPreview(chat.lastMessage) : 'Личные заметки')}</div>
              </div>
              <div className="chat-item-meta">
                {unreadCounts[chat.id] > 0 && (
                  <span className="unread-badge">{unreadCounts[chat.id]}</span>
                )}
              </div>
            </div>
          ))}
          {chats.filter(c => c.name !== 'Своё').map((chat) => {
            const otherParticipant = chat.participants?.find((p) => p.userId !== user?.id);
            const isOnline = otherParticipant && onlineUsers[otherParticipant.userId];
            const chatAvatar = absUrl(chat.avatarUrl) || (otherParticipant?.avatarUrl ? absUrl(otherParticipant.avatarUrl) : null);
            const chatDisplayName = chat.name || otherParticipant?.displayName || 'Чат';
            return (
            <div key={chat.id}
              className={`chatlist-item ${activeChat === chat.id ? 'active' : ''}`}
              onClick={() => { setActiveChat(chat.id); setMobileOpen(false); onCloseAIChat?.(); }}>
              <div className="chat-item-avatar">
                {chatAvatar ? (
                  <img src={chatAvatar} alt="" onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', background: '#e8e8ff', borderRadius: '50%', width: '100%', height: '100%' }}>
                    {getAvatarEmoji(chatDisplayName)}
                  </div>
                )}
                {chat.isGroup && <span className="group-badge">G</span>}
                {chat.isChannel && <span className="channel-badge">📢</span>}
                {isOnline && !chat.isChannel && <span className="online-dot"></span>}
              </div>
              <div className="chat-item-info">
                <div className="chat-item-name">{chat.name || otherParticipant?.displayName || 'Чат'}{chat.isBlocked ? ' 🚫' : ''}</div>
                <div className="chat-item-last-msg">{chat.isBlocked ? 'Вы заблокировали этого пользователя' : (typingUsers[chat.id] ? 'Печатает...' : (chat.lastMessage ? lastMsgPreview(chat.lastMessage) : ''))}</div>
              </div>
              <div className="chat-item-meta">
                {unreadCounts[chat.id] > 0 && (
                  <span className="unread-badge">{unreadCounts[chat.id]}</span>
                )}
              </div>
            </div>
            );
          })}
        </div>
        {showCreateGroup && (
          <div className="modal-overlay" onClick={() => setShowCreateGroup(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Создать групповой чат</h3>
              {groupError && <div className="search-error">{groupError}</div>}
              <div className="form-group">
                <label>Название группы</label>
                <input type="text" value={groupName} onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Название группы..." style={{ width: '100%', padding: '8px', marginTop: '4px', boxSizing: 'border-box' }} />
              </div>
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label>Добавить участников</label>
                <input type="text" value={groupSearch} onChange={handleGroupSearch}
                  placeholder="Поиск пользователей..." style={{ width: '100%', padding: '8px', marginTop: '4px', boxSizing: 'border-box' }} />
                {groupSearchResults.length > 0 && (
                  <div className="search-results" style={{ position: 'relative', marginTop: '4px' }}>
                    {groupSearchResults.map((u) => (
                      <div key={u.id} className="search-result-item" onClick={() => addGroupParticipant(u)}>
                        {u.avatarUrl ? (
                          <img src={absUrl(u.avatarUrl)} alt="" className="avatar-sm"
                            onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
                        ) : (
                          <div className="avatar-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', background: '#e8e8ff', borderRadius: '50%' }}>
                            {getAvatarEmoji(u.displayName)}
                          </div>
                        )}
                        <div>
                          <div className="search-name">{u.displayName}{u.isBlocked && ' 🚫'}</div>
                          <div className="search-email">{u.email}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {groupParticipants.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                  {groupParticipants.map((p) => (
                    <span key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#e8e8ff', padding: '4px 8px', borderRadius: '12px', fontSize: '13px' }}>
                      {p.displayName}
                      <span onClick={() => removeGroupParticipant(p.id)} style={{ cursor: 'pointer', fontWeight: 'bold' }}>✕</span>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button onClick={createGroup} style={{ flex: 1, padding: '10px', background: '#1a1a2e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Создать</button>
                <button onClick={() => { setShowCreateGroup(false); setGroupName(''); setGroupParticipants([]); setGroupError(''); }}
                  style={{ flex: 1, padding: '10px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Отмена</button>
              </div>
            </div>
          </div>
        )}
        {showChannels && (
          <div className="modal-overlay" onClick={() => setShowChannels(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="user-info-header">
                <h3 style={{ margin: 0 }}>📢 Каналы</h3>
                <button className="modal-close-btn" onClick={() => setShowChannels(false)}>✕</button>
              </div>
              <div className="user-info-body">
                <button className="action-btn" style={{ width: '100%', marginBottom: '12px' }}
                  onClick={() => setShowCreateChannel(true)}>+ Создать канал</button>
                {showCreateChannel && (
                  <div style={{ borderTop: '1px solid var(--border, #e0e0e0)', paddingTop: '12px' }}>
                    {channelError && <div className="search-error">{channelError}</div>}
                    <div className="form-group" style={{ marginTop: '12px' }}>
                      <label>Название канала</label>
                      <input type="text" value={channelName} onChange={(e) => { setChannelName(e.target.value); setChannelError(''); }}
                        placeholder="Название канала..." style={{ width: '100%', padding: '8px', marginTop: '4px', boxSizing: 'border-box' }} />
                    </div>
                    <div className="form-group" style={{ marginTop: '12px' }}>
                      <label>Аватарка канала</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px' }}>
                        <div className="channel-avatar-preview" style={{ width: '44px', height: '44px', borderRadius: '50%', overflow: 'hidden', background: '#e8e8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                          {channelAvatarUrl ? <img src={absUrl(channelAvatarUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '📢'}
                        </div>
                        <button className="action-btn" onClick={() => channelAvatarInputRef.current?.click()}
                          style={{ flex: 1, padding: '8px' }}>
                          {channelAvatarUrl ? 'Изменить' : 'Выбрать изображение'}
                        </button>
                        <input type="file" ref={channelAvatarInputRef} hidden accept="image/*" onChange={handleChannelAvatarFile} />
                      </div>
                    </div>
                    <div className="form-group" style={{ marginTop: '12px' }}>
                      <label>Видимость</label>
                      <select value={channelVisibility} onChange={(e) => setChannelVisibility(e.target.value)}
                        style={{ width: '100%', padding: '8px', marginTop: '4px' }}>
                        <option value="PUBLIC">Публичный (видят все)</option>
                        <option value="PRIVATE">Личный (только по приглашению)</option>
                      </select>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '14px' }}>
                      <input type="checkbox" checked={channelComments} onChange={(e) => setChannelComments(e.target.checked)} />
                      Разрешить комментарии подписчикам
                    </label>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                      <button onClick={handleCreateChannel} style={{ flex: 1, padding: '10px', background: '#1a1a2e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Создать</button>
                      <button onClick={() => { setShowCreateChannel(false); setChannelName(''); setChannelAvatarUrl(''); setChannelError(''); }}
                        style={{ flex: 1, padding: '10px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Отмена</button>
                    </div>
                  </div>
                )}
                {!showCreateChannel && (
                  <>
                    <div className="user-info-status muted" style={{ marginBottom: '8px' }}>Доступные публичные каналы:</div>
                    {channelsError && (
                      <div className="search-error">
                        {channelsError}
                        <button className="chatlist-retry-btn" style={{ marginTop: '6px' }} onClick={() => fetchChannels().catch(() => {})}>Повторить</button>
                      </div>
                    )}
                    {!channelsError && channels.length === 0 && <div className="user-info-status muted">Пока нет доступных каналов</div>}
                    {channels.map((c) => (
                      <button key={c.id} className="forward-chat-item"
                        onClick={async () => { try { await subscribeChannel(c.id); } catch (e) {} }}>
                        <span className="forward-chat-avatar">📢</span>
                        <span style={{ flex: 1 }}>{c.name}</span>
                        <span style={{ fontSize: '12px', color: 'var(--system-text, #888)' }}>
                          {c.visibility === 'PRIVATE' ? '🔒' : '🌍'} {c.allowComments ? '💬' : '🔇'}
                        </span>
                        <span className="unread-badge" style={{ background: '#1a1a2e', color: 'white' }}>+</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {mobileOpen && <div className="chatlist-backdrop" onClick={() => setMobileOpen(false)} />}
    </>
  );
}
